import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from '../public/locales/en/translation.json';
import es from '../public/locales/es/translation.json';

/**
 * Portal UI strings (chrome, Kit hub, tools). Guide CONTENT carries both
 * languages inside its own objects (src/kit/content). Language choice is
 * per-origin (localStorage) — no shared cookie with the consumer apps by ruling.
 */
const KEY = 'runner_lang';
function initial(): 'en' | 'es' {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'es' || v === 'en') return v;
  } catch {
    /* no storage */
  }
  return navigator.language?.toLowerCase().startsWith('es') ? 'es' : 'en';
}

void i18n.use(initReactI18next).init({
  lng: initial(),
  fallbackLng: 'en',
  supportedLngs: ['en', 'es'],
  resources: { en: { translation: en }, es: { translation: es } },
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function syncDocumentLang(lng: string) {
  // Screen readers pick pronunciation rules from <html lang>; a Spanish UI
  // under lang="en" is read with English rules (WCAG 3.1.1).
  if (typeof document !== 'undefined') document.documentElement.lang = lng.startsWith('es') ? 'es' : 'en';
}
syncDocumentLang(i18n.language);
type Lang = 'en' | 'es';
type Saver = (l: Lang) => Promise<unknown>;
let saveToServer: Saver | null = null;
let adopting = false;

const asLang = (lng: string): Lang => (lng.startsWith('es') ? 'es' : 'en');

i18n.on('languageChanged', (lng) => {
  syncDocumentLang(lng);
  try {
    localStorage.setItem(KEY, lng);
  } catch {
    /* no storage */
  }
  if (!adopting) saveToServer?.(asLang(lng)).catch(() => {});
});

/**
 * The Runner's language lives on their profile (Rod, 2026-09-28) so the portal,
 * the app and every push and email agree. Wired once /runner/me has loaded.
 */
export function setLanguageSaver(fn: Saver | null): void {
  saveToServer = fn;
}

/** A stored choice wins over this browser's; a profile with none gets this browser's. */
export async function adoptServerLanguage(me: { runnerId: string | null; preferredLanguage?: Lang | null }): Promise<void> {
  if (me.preferredLanguage === 'en' || me.preferredLanguage === 'es') {
    if (asLang(i18n.language) === me.preferredLanguage) return;
    adopting = true;
    try {
      await i18n.changeLanguage(me.preferredLanguage);
    } finally {
      adopting = false;
    }
    return;
  }
  if (me.runnerId) saveToServer?.(asLang(i18n.language)).catch(() => {});
}

export default i18n;
